import React, { useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Grid,
  Paper,
  Divider,
  InputAdornment,
  List,
  IconButton,
  Stack,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    compulsoryFieldBasedOnCategory,
  disableBasedOnCategory,
  lensTypes,
  productCategories,
} from "../constants";

export default function CartAdd({ cartItems, setCartItems }) {
  // Style for disabled text fields
  const disabledFieldStyle = {
    "& .Mui-disabled": {
      backgroundColor: "#f5f5f5",
    },
  };
  // Utility function to prevent wheel scrolling on number inputs
  const preventWheelScroll = (e) => {
    e.target.blur();
  };

  // Utility function to prevent negative sign input
  const preventNegativeInput = (e) => {
    if (e.key === "-" || e.keyCode === 45) {
      e.preventDefault();
    }
  };
  // State for form fields
  const [itemName, setItemName] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [lensType, setLensType] = useState("");
  const [frameMRP, setFrameMRP] = useState("");
  const [frameDiscount, setFrameDiscount] = useState("");
  const [lensMRP, setLensMRP] = useState("");
  const [lensDiscount, setLensDiscount] = useState("");
  const [otherMRP, setOtherMRP] = useState("");
  const [otherDiscount, setOtherDiscount] = useState("");
  const [frameDiscountError, setFrameDiscountError] = useState(false);
  const [lensDiscountError, setLensDiscountError] = useState(false);
  const [otherDiscountError, setOtherDiscountError] = useState(false);

  // Calculate totals
  const calculateNetPrice = (mrp, discount) => {
    const mrpValue = parseFloat(mrp) || 0;
    const discountValue = parseFloat(discount) || 0;
    return (mrpValue - discountValue).toFixed(2);
  };

  const totalMRP =
    parseFloat(frameMRP || 0) +
    parseFloat(lensMRP || 0) +
    parseFloat(otherMRP || 0);
  const totalDiscount =
    parseFloat(frameDiscount || 0) +
    parseFloat(lensDiscount || 0) +
    parseFloat(otherDiscount || 0);
  const totalNetPrice = totalMRP - totalDiscount;

  // Calculate cart totals
  const cartTotalMRP = cartItems.reduce(
    (sum, item) => sum + item.totalPrice,
    0
  );
  const cartTotalDiscount = cartItems.reduce(
    (sum, item) => sum + item.totalDiscount,
    0
  );
  const cartTotalNetPrice = cartTotalMRP - cartTotalDiscount;

  // Handle adding item to cart
  const handleAddToCart = () => {
    if (!itemName || !productCategory) {
      alert("Please enter item name and product category");
      return;
    }

    const newItem = {
      itemId: Date.now().toString(), // Unique ID for the item
      itemName,
      category: productCategories[productCategory],
      lensType,
      quantity: 1,
      totalFramePrice: parseFloat(frameMRP || 0),
      totalFrameDiscount: parseFloat(frameDiscount || 0),
      totalLensPrice: parseFloat(lensMRP || 0),
      totalLensDiscount: parseFloat(lensDiscount || 0),
      totalOtherPrice: parseFloat(otherMRP || 0),
      totalOtherDiscount: parseFloat(otherDiscount || 0),
      totalPrice: totalMRP,
      totalDiscount: totalDiscount,
    };

    setCartItems([...cartItems, newItem]);
    resetForm();
    setItemName("");
    setLensType("");
    setProductCategory("");
  };

  // Reset form fields
  const resetForm = () => {
    setFrameMRP("");
    setFrameDiscount("");
    setLensMRP("");
    setLensDiscount("");
    setOtherMRP("");
    setOtherDiscount("");
  };

  const handleProductCategoryChange = (category) => {
    setProductCategory(category);
    resetForm();
  };

  // Handle removing item from cart
  const handleRemoveFromCart = (itemId) => {
    setCartItems(cartItems.filter((item) => item.itemId !== itemId));
  };

  const disableAddCartBasedOnCategory = () => {
    if (productCategory === "") return false;
    if (compulsoryFieldBasedOnCategory.lensMRP.includes(productCategory)) {
      return lensMRP === "";
    }
    if (compulsoryFieldBasedOnCategory.otherMRP.includes(productCategory)) {
      return otherMRP === "";
    }
    return frameMRP === "" || lensMRP === "";
  };

  return (
    <Grid container spacing={2} sx={{ height: "calc(100vh - 200px)" }}>
      {/* Left side - Add Item Form */}
      <Grid item xs={12} md={7} sx={{ height: "100%" }}>
        <Box
          sx={{
            p: 2,
            height: "100%",
            overflow: "auto",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          {/* Item Details Section */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Add Item to Cart
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name"
                variant="outlined"
                size="small"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="product-category-label">
                  Product Category
                </InputLabel>
                <Select
                  labelId="product-category-label"
                  id="product-category"
                  value={productCategory}
                  label="Product Category"
                  size="small"
                  onChange={(e) => {
                    handleProductCategoryChange(e.target.value);
                  }}
                >
                  {Object.keys(productCategories).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="lens-type-label">Lens Type</InputLabel>
                <Select
                  labelId="lens-type-label"
                  id="lens-type"
                  value={lensType}
                  label="Lens Type"
                  size="small"
                  onChange={(e) => setLensType(e.target.value)}
                >
                  {lensTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2} direction="column" sx={{ mt: 2 }}>
            {/* Frame Pricing */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: "medium" }}
                >
                  Frame
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="MRP"
                      type="number"
                      size="small"
                      value={frameMRP}
                      onChange={(e) => setFrameMRP(e.target.value)}
                      inputProps={{ min: 0 }}
                      onWheel={preventWheelScroll}
                      onKeyDown={preventNegativeInput}
                      disabled={disableBasedOnCategory[productCategory].frame}
                      sx={disabledFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Discount"
                      type="number"
                      size="small"
                      value={frameDiscount}
                      onChange={(e) => {
                        const discountValue = parseFloat(e.target.value) || 0;
                        const mrpValue = parseFloat(frameMRP) || 0;

                        if (discountValue > mrpValue) {
                          setFrameDiscount(mrpValue);
                          setFrameDiscountError(true);
                        } else {
                          setFrameDiscount(e.target.value);
                          setFrameDiscountError(false);
                        }
                      }}
                      error={frameDiscountError}
                      helperText={
                        frameDiscountError ? "Discount can't exceed MRP" : ""
                      }
                      inputProps={{ min: 0 }}
                      onWheel={preventWheelScroll}
                      onKeyDown={preventNegativeInput}
                      disabled={disableBasedOnCategory[productCategory].frame}
                      sx={disabledFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Net: ₹{calculateNetPrice(frameMRP, frameDiscount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Lens Pricing */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: "medium" }}
                >
                  Lens
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="MRP"
                      type="number"
                      size="small"
                      value={lensMRP}
                      onChange={(e) => setLensMRP(e.target.value)}
                      inputProps={{ min: 0 }}
                      onWheel={preventWheelScroll}
                      onKeyDown={preventNegativeInput}
                      disabled={disableBasedOnCategory[productCategory].lens}
                      sx={disabledFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Discount"
                      type="number"
                      size="small"
                      value={lensDiscount}
                      onChange={(e) => {
                        const discountValue = parseFloat(e.target.value) || 0;
                        const mrpValue = parseFloat(lensMRP) || 0;

                        if (discountValue > mrpValue) {
                          setLensDiscount(mrpValue);
                          setLensDiscountError(true);
                        } else {
                          setLensDiscount(e.target.value);
                          setLensDiscountError(false);
                        }
                      }}
                      error={lensDiscountError}
                      helperText={
                        lensDiscountError ? "Discount can't exceed MRP" : ""
                      }
                      inputProps={{ min: 0 }}
                      onWheel={preventWheelScroll}
                      onKeyDown={preventNegativeInput}
                      disabled={disableBasedOnCategory[productCategory].lens}
                      sx={disabledFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Net: ₹{calculateNetPrice(lensMRP, lensDiscount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Other Pricing */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: "medium" }}
                >
                  Others
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="MRP"
                      type="number"
                      size="small"
                      value={otherMRP}
                      onChange={(e) => setOtherMRP(e.target.value)}
                      inputProps={{ min: 0 }}
                      onWheel={preventWheelScroll}
                      onKeyDown={preventNegativeInput}
                      disabled={disableBasedOnCategory[productCategory].other}
                      sx={disabledFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Discount"
                      type="number"
                      size="small"
                      value={otherDiscount}
                      onChange={(e) => {
                        const discountValue = parseFloat(e.target.value) || 0;
                        const mrpValue = parseFloat(otherMRP) || 0;

                        if (discountValue > mrpValue) {
                          setOtherDiscount(mrpValue);
                          setOtherDiscountError(true);
                        } else {
                          setOtherDiscount(e.target.value);
                          setOtherDiscountError(false);
                        }
                      }}
                      error={otherDiscountError}
                      helperText={
                        otherDiscountError ? "Discount can't exceed MRP" : ""
                      }
                      inputProps={{ min: 0 }}
                      onWheel={preventWheelScroll}
                      onKeyDown={preventNegativeInput}
                      disabled={disableBasedOnCategory[productCategory].other}
                      sx={disabledFieldStyle}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Net: ₹{calculateNetPrice(otherMRP, otherDiscount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Item Pricing Total */}
          <Paper elevation={1} sx={{ p: 2, mt: 1 }}>
            <Grid container spacing={1} direction="column">
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  Item MRP: ₹{totalMRP.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  Item Discount: ₹{totalDiscount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Item Net: ₹{totalNetPrice.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Add to Cart Button */}
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <button
              className={
                !itemName || !productCategory || totalNetPrice <= 0 || disableAddCartBasedOnCategory()
                  ? "border-gray-500 bg-gray-500 text-gray-100 border cursor-default rounded-md w-40 p-1 text-sm font-semibold"
                  : "border-blue-600 bg-blue-600 text-gray-100 border cursor-pointer rounded-md w-40 p-1 text-sm font-semibold"
              }
              onClick={handleAddToCart}
              disabled={!itemName || !productCategory || totalNetPrice <= 0 || disableAddCartBasedOnCategory()}
            >
              Add to Cart
            </button>
          </Box>
        </Box>
      </Grid>

      {/* Right side - Cart Summary */}
      <Grid item xs={12} md={5} sx={{ height: "100%" }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            height: "100%",
            backgroundColor: "#f5f5f5",
            position: "sticky",
            top: 20,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Cart Summary {cartItems.length > 0 && `(${cartItems.length} items)`}
          </Typography>

          {cartItems.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="subtitle2" color="text.secondary">
                Your cart is empty.
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ maxHeight: "400px", overflow: "auto" }}>
                {cartItems.map((item) => (
                  <Card
                    key={item.id}
                    sx={{
                      mb: 1,
                      border: "1px solid #e0e0e0",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          component="div"
                          sx={{ fontWeight: "medium" }}
                        >
                          {item.itemName}
                        </Typography>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          size="small"
                          onClick={() => handleRemoveFromCart(item.itemId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mb: 1, mt: 0.5 }}
                      >
                        <Chip
                          label={item.category}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {item.lensType && (
                          <Chip
                            label={item.lensType}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            MRP: ₹{item.totalPrice.toFixed(2)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Discount: ₹{item.totalDiscount.toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="subtitle2"
                        sx={{ mt: 1, fontWeight: "medium" }}
                      >
                        Net: ₹
                        {(item.totalPrice - item.totalDiscount).toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </List>

              <Divider sx={{ mb: 2 }} />

              {/* Cart Total */}
              <Grid container spacing={1} sx={{ ml: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Total MRP: ₹{cartTotalMRP.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    Total Discount: ₹{cartTotalDiscount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Net Amount: ₹{cartTotalNetPrice.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
